import React from 'react';
import { useDiscrepancySettings } from '@rsos/capstone/discrepancy-reporting/hooks';
import Loader from '@rsos/components/capstone/Loader';
import { Button, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import {
  Wrapper,
  FormSection,
  FormRow,
  ButtonRow,
  HeaderSection,
  LoaderWrapper,
} from './DiscrepancyReporting.styles';

export function DiscrepancyReporting() {
  const { settings, setSettings, loading, saving, save } =
    useDiscrepancySettings();

  // Helper to reduce duplication in onChange handlers
  const handleFieldChange =
    <K extends keyof typeof settings>(
      field: K,
      transform?: (value: string) => string,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSettings(prev => ({
        ...prev,
        [field]: transform ? transform(e.target.value) : e.target.value,
      }));
    };

  const handleSave = async () => {
    const success = await save();
    if (success) {
      notifySuccess('Discrepancy reporting settings saved successfully');
    } else {
      notifyError('Failed to save discrepancy reporting settings');
    }
  };

  return (
    <>
      <HeaderSection>
        <Text size="header" fontWeight="semibold" color="primaryText">
          Discrepancy Reporting
        </Text>
        <Text size="large" color="primaryText">
          Configure ALI discrepancy reporting settings for INdigital
          integration.
        </Text>
      </HeaderSection>
      <Wrapper>
        {loading && (
          <LoaderWrapper>
            <Loader text="Loading settings..." />
          </LoaderWrapper>
        )}
        {!loading && (
          <>
            <FormSection>
              <FormRow>
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Target State
                </Text>
                <Text size="small" color="secondaryText">
                  Two-letter US state code (e.g., NY, CA, TX)
                </Text>
                <InputText
                  id="targetState"
                  value={settings.targetState}
                  onChange={handleFieldChange('targetState', v =>
                    v.toUpperCase().slice(0, 2),
                  )}
                  placeholder="e.g., NY"
                />
              </FormRow>

              <FormRow>
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Client ID
                </Text>
                <InputText
                  id="clientID"
                  value={settings.clientID}
                  onChange={handleFieldChange('clientID')}
                  placeholder="Enter INdigital client ID"
                />
              </FormRow>

              <FormRow>
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Client Secret
                </Text>
                <InputText
                  id="clientSecret"
                  value={settings.clientSecret}
                  onChange={handleFieldChange('clientSecret')}
                  placeholder="Enter INdigital client secret"
                />
              </FormRow>
            </FormSection>

            <ButtonRow>
              <Button
                btnType="success"
                isLoadingProp={saving}
                onClick={handleSave}
              >
                Save
              </Button>
            </ButtonRow>
          </>
        )}
      </Wrapper>
    </>
  );
}
